import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { acquirePdf, pdfPageAspect, thumbnailWidth } from '@/lib/pdfjs';
import { DEFAULT_PAGE_ASPECT, dotWindow, pdfPagePlacement, pdfStackLayout, type StackBox } from '@/lib/pdfStack';
import { PaperSkeleton, PdfThumbnail } from '@/components/items/PdfThumbnail';
import { cn } from '@/lib/utils';
import { SwipeCarousel } from './SwipeCarousel';

/** A swipe longer than this (px) turns a tile's page. */
const TURN_PX = 24;

interface PdfDoc {
  doc: PDFDocumentProxy | null;
  pageCount: number;
  /** Height / width of the first page. */
  aspect: number;
  failed: boolean;
}

/** The document at `url`, loaded once `ref` comes within 400px of the screen (shared with other cards through acquirePdf). */
function usePdfDoc(url: string, ref: React.RefObject<HTMLElement>): PdfDoc {
  const [near, setNear] = useState(false);
  const [state, setState] = useState<PdfDoc>({ doc: null, pageCount: 0, aspect: DEFAULT_PAGE_ASPECT, failed: false });

  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && setNear(true), { rootMargin: '400px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [near, ref]);

  useEffect(() => {
    if (!near || !url) return;
    let cancelled = false;
    const { promise, release } = acquirePdf(url);
    promise
      .then(async (pdf) => {
        const aspect = await pdfPageAspect(pdf, 1);
        if (!cancelled) setState({ doc: pdf, pageCount: pdf.numPages, aspect, failed: false });
      })
      .catch((err) => {
        console.error('[PdfPages] Failed to load PDF', err);
        if (!cancelled) setState((s) => ({ ...s, failed: true }));
      });
    return () => {
      cancelled = true;
      setState((s) => ({ ...s, doc: null }));
      release();
    };
  }, [url, near]);

  return state;
}

/** The element's width and height, tracked on width changes only (its height follows from its ratio). */
function useBoxSize(ref: React.RefObject<HTMLElement>): { width: number; height: number } {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    // Width only: nothing here changes the box's own size, but guard anyway (CLAUDE.md rule 1)
    let lastWidth = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (Math.abs(el.clientWidth - lastWidth) <= 1) return;
      lastWidth = el.clientWidth;
      measure();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
}

const px = (b: StackBox): React.CSSProperties => ({ left: b.left, top: b.top, width: b.width, height: b.height });

/**
 * A PDF in the grid, as the desktop card draws it: the current page in front,
 * the next one peeking out behind, a counter and dots. A sideways swipe turns
 * the page; a tap still opens the item (a swipe never does). Pinches and
 * vertical scrolls pass through to the grid.
 */
export function PdfStackTile({ url, size }: { url: string; size: 'regular' | 'small' | 'tiny' }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const { doc, pageCount, aspect, failed } = usePdfDoc(url, rootRef);
  const box = useBoxSize(rootRef);
  const [index, setIndex] = useState(0);
  const indexRef = useRef(0);
  const swipedAt = useRef(0);

  const last = Math.max(0, pageCount - 1);
  const current = Math.min(index, last);
  const multi = !!doc && pageCount > 1 && size !== 'tiny';
  const flipped = multi && current === last;
  const layout = pdfStackLayout(box.width, box.height, aspect, multi);
  const front = flipped ? layout.frontFlipped : layout.front;
  const renderWidth = thumbnailWidth(layout.front.width || 120);

  useEffect(() => {
    indexRef.current = current;
  }, [current]);

  // Sideways swipes turn the page; anything else is the grid's
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !multi) return;
    let startX = 0;
    let startY = 0;
    let mode: 'pending' | 'swipe' | 'pass' = 'pass';
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        mode = 'pass';
        return;
      }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      mode = 'pending';
    };
    const onMove = (e: TouchEvent) => {
      if (mode === 'pass' || !e.touches[0]) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (mode === 'pending') {
        if (Math.hypot(dx, dy) < 8) return;
        mode = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'swipe' : 'pass';
      }
      if (mode === 'swipe' && e.cancelable) e.preventDefault();
    };
    const onEnd = (e: TouchEvent) => {
      if (mode !== 'swipe') {
        mode = 'pass';
        return;
      }
      mode = 'pass';
      swipedAt.current = Date.now();
      const dx = (e.changedTouches[0]?.clientX ?? startX) - startX;
      if (Math.abs(dx) < TURN_PX) return;
      setIndex(Math.max(0, Math.min(last, indexRef.current + (dx < 0 ? 1 : -1))));
    };
    // The tap that ends a swipe must not open the item
    const onClickCapture = (e: MouseEvent) => {
      if (Date.now() - swipedAt.current < 450) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    el.addEventListener('click', onClickCapture, true);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, [multi, last]);

  const dots = multi && size === 'regular' ? dotWindow(pageCount, current) : [];
  const pageClass = 'absolute overflow-hidden rounded-[4px] bg-white ring-1 ring-black/5 transition-[left,top,width,height,opacity] duration-300 ease-out motion-reduce:transition-none';

  return (
    <div ref={rootRef} className="absolute inset-0" aria-label={pageCount > 1 ? `${pageCount} pages` : undefined}>
      {box.width > 0 &&
        (doc && !failed ? (
          Array.from({ length: pageCount }, (_, i) => {
            const offset = i - current;
            if (Math.abs(offset) > 2) return null;
            const { box: b, z, visible } = pdfPagePlacement(layout, multi ? offset : 0, flipped);
            if (!multi && offset !== 0) return null;
            return (
              <div
                key={i}
                className={cn(pageClass, offset === 0 ? 'shadow-[0_8px_20px_-8px_rgba(0,0,0,0.35)]' : 'shadow-[0_4px_12px_-6px_rgba(0,0,0,0.25)]')}
                style={{ ...px(b), zIndex: z, opacity: visible ? 1 : 0 }}
              >
                {/* Only the page in front and its neighbours render */}
                {Math.abs(offset) <= 1 ? <PdfThumbnail url={url} doc={doc} pageNumber={i + 1} renderWidth={renderWidth} className="[&>img]:object-top" /> : <PaperSkeleton pulse={false} />}
              </div>
            );
          })
        ) : (
          <div className={cn(pageClass, 'shadow-[0_8px_20px_-8px_rgba(0,0,0,0.35)]')} style={px(layout.front)}>
            <PaperSkeleton pulse={!failed} />
          </div>
        ))}

      {multi && (
        <span
          className={cn(
            'pointer-events-none absolute z-[4] rounded-full bg-paper-raised/90 font-mono tabular-nums text-ink shadow-sm transition-[left,top] duration-300 ease-out motion-reduce:transition-none',
            size === 'regular' ? 'px-1.5 text-[10px] leading-[16px]' : 'px-1 text-[9px] leading-[13px]',
          )}
          style={{ left: front.left + front.width - (size === 'regular' ? 6 : 4), top: front.top + (size === 'regular' ? 6 : 4), transform: 'translateX(-100%)' }}
        >
          {current + 1}/{pageCount}
        </span>
      )}

      {dots.length > 0 && (
        <div className="pointer-events-none absolute z-[4] flex items-center justify-center gap-1" style={{ left: front.left, top: layout.dotsTop, width: front.width, height: 22 }}>
          {dots.map((i, n) => (
            <span
              key={i}
              className={cn(
                'block rounded-full transition-all duration-300 ease-out',
                i === current ? 'h-1.5 w-3 bg-ink/70' : 'h-1.5 w-1.5 bg-ink/25',
                pageCount > dots.length && ((n === 0 && i > 0) || (n === dots.length - 1 && i < last)) && 'scale-[0.6]',
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A PDF in the detail view: every page, swiped one at a time like a carousel
 * post (the same track, counter and dots). Each page sits whole on the paper,
 * at its own shape. A tap on a page opens the reader there.
 */
export function PdfPagesCarousel({ url, label, onOpenPage }: { url: string; label: string; onOpenPage?: (page: number) => void }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const { doc, pageCount, aspect, failed } = usePdfDoc(url, rootRef);
  const box = useBoxSize(rootRef);
  const [index, setIndex] = useState(0);

  // The page fitted inside the slide with a margin, at the first page's shape
  const pad = 16;
  const availW = Math.max(0, box.width - pad * 2);
  const availH = Math.max(0, box.height - pad * 2 - (pageCount > 1 ? 28 : 0));
  const pageW = Math.min(availW, availH / (aspect || DEFAULT_PAGE_ASPECT));
  const pageH = pageW * (aspect || DEFAULT_PAGE_ASPECT);
  const renderWidth = thumbnailWidth(pageW || 300);

  const page = (i: number) => (
    <button
      type="button"
      onClick={() => onOpenPage?.(i + 1)}
      aria-label={`Read from page ${i + 1}`}
      className="absolute inset-0 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-signal"
    >
      <span
        className="absolute left-1/2 overflow-hidden rounded-[4px] bg-white shadow-[0_10px_28px_-10px_rgba(0,0,0,0.4)] ring-1 ring-black/5"
        style={{ width: pageW, height: pageH, top: pad + Math.max(0, (availH - pageH) / 2), transform: 'translateX(-50%)' }}
      >
        {doc && Math.abs(i - index) <= 1 ? <PdfThumbnail url={url} doc={doc} pageNumber={i + 1} renderWidth={renderWidth} /> : <PaperSkeleton pulse={!failed && Math.abs(i - index) <= 1} />}
      </span>
    </button>
  );

  return (
    <div ref={rootRef} className="absolute inset-0 bg-paper-sunken">
      {box.width > 0 &&
        (doc && pageCount > 1 ? (
          <SwipeCarousel count={pageCount} renderSlide={page} label={label} onIndexChange={setIndex} />
        ) : (
          page(0)
        ))}
    </div>
  );
}
